import React, { useCallback, useState } from 'react';
import { HelperText, useTheme } from 'react-native-paper';
import { CompositeScreenProps } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { useMutation, useQueryClient } from 'react-query';
import { useI18n } from '../../context/I18nContext';
import ConfirmationRow from '../../payment/components/ConfirmationRow';
import Container from '../../components/Container';
import { NavigationDialog } from '../CommonDialogs';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { useMixpanel } from '../../mixpanel/MixpanelContext';
import Spacer from '../../components/Spacer';
import { cancelSubscription, invalidateSubscriptionData, useGetProductSubscription } from '../../apis/subscriptionApis';
import { flattenDict } from '../../utils/helpers';
import { ProductSubscriptionStatus } from '../../types/appsync-types';
import { MapStackRouteParams } from '../../navigators/MapStackNavigator';

export interface ConfirmOneTimePurchaseCancelDialogRouteParams {
  id: string;
}

type Props = CompositeScreenProps<
  StackScreenProps<MapStackRouteParams, 'ConfirmOneTimePurchaseCancelDialog'>,
  AppDrawerScreenProp
>;

export function ConfirmOneTimePurchaseCancelDialog({ navigation, route }: Props) {
  const { colors } = useTheme();
  const mp = useMixpanel();
  const queryClient = useQueryClient();
  const { I18n } = useI18n();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // TODO: Check if this is getting the subscription from cache or fetching it from backend again, cache would be preferred
  const getProductSubscriptionResult = useGetProductSubscription(route.params.id);

  // TODO: handle either of these being undefined
  const subscription = getProductSubscriptionResult.data?.data?.getProductSubscription;
  const product = subscription?.product;

  const isActionRequired = subscription?.status === ProductSubscriptionStatus.REQUIRES_ACTION;

  const cancelSubscriptionMutation = useMutation(
    async (input: Parameters<typeof cancelSubscription>) => {
      return cancelSubscription(...input);
    },
    {
      onSuccess(data) {
        mp?.track('One-time payment cancel success', flattenDict('subscription', data.data?.makeAProductSubscription));
        mp?.getPeople().increment('One-time payment cancellations', 1);
        invalidateSubscriptionData(queryClient);
        navigation.goBack();
      },
      onError(error: any) {
        mp?.track('One-time payment cancel error', error ?? null);
        setErrorMessage(`${I18n.t('oneTimePurchase.confirmOneTimePurchaseCancelModal.failTitle')}: ${error?.message}`);
      },
    },
  );

  const handleUserDismissed = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return (
    <NavigationDialog
      style={{ backgroundColor: colors.elevation.level2 }} // TODO: is this correct?
      title={I18n.t('oneTimePurchase.confirmOneTimePurchaseCancelModal.title')}
      description={''}
      onDismiss={handleUserDismissed}
      actions={[
        {
          title: I18n.t('oneTimePurchase.confirmOneTimePurchaseCancelModal.cancel'),
          onPress: handleUserDismissed,
          disable: cancelSubscriptionMutation.isLoading,
        },
        {
          title: I18n.t('oneTimePurchase.confirmOneTimePurchaseCancelModal.confirm'),
          loading: cancelSubscriptionMutation.isLoading,
          disable: cancelSubscriptionMutation.isLoading || !isActionRequired,
          onPress: () => {
            return cancelSubscriptionMutation.mutate([{ id: route.params.id }]);
          },
        },
      ]}
      iconName={'check'}
    >
      {/* Product */}
      <ConfirmationRow title={product?.name ?? '?'} iconName="clock" />

      <Spacer />

      {/* Error text */}
      <Container>
        <HelperText type={'error'} visible={!!errorMessage}>
          {errorMessage}
        </HelperText>
      </Container>
    </NavigationDialog>
  );
}
